//utils
import { makeDownstreamApiCall } from "@payment-client/utils/Api";

//interfaces
import {
    AddMoneyAggregateResponse,
    AddMoneyDataApiResponseType,
    GetBalanceApiResponse,
    GetEligibilityApiModel,
    GetOffersApiModel,
} from "@payment-client/interfaces/SwiggyMoney";
import { ApiResponse } from "@payment-client/interfaces/Api";
import { isEmpty } from "@swiggy-private/js-utils";

export const ENDPOINT = {
    BALANCE_API: "/payment/presentation/v1/swiggypay/getbalance",
    CHECK_ADD_MONEY_ELIGIBILITY: "/payment/add-money/v1/order/transaction/eligibility",
    FETCH_ADD_MONEY_OFFERS: "/payment/add-money/v1/order/offers",
};

const ADD_MONEY_STATUS_CODES = {
    SUCCESS: 0,
    BALANCE_FAILURE: 4,
    ELIGIBILITY_FAILURE: 5,
    BALANCE_ELIGIBILITY_FAILURE: 6,
};

const STATUS_CODE_MESSAGE_MAPPING = {
    [ADD_MONEY_STATUS_CODES.SUCCESS]: "Fetch Add Money Details Request Executed Successfully",
    [ADD_MONEY_STATUS_CODES.BALANCE_FAILURE]: "Balance Details Request Failed",
    [ADD_MONEY_STATUS_CODES.ELIGIBILITY_FAILURE]: "Eligibility Details Request Failed",
    [ADD_MONEY_STATUS_CODES.BALANCE_ELIGIBILITY_FAILURE]: "Balance and Eligibility Request Failed",
};

const getAggregateStatusDetails = (
    balanceSuccess: boolean,
    eligibilitySuccess: boolean,
): {
    statusCode: number;
    statusMessage: string;
} => {
    let statusCode;
    if (balanceSuccess && eligibilitySuccess) {
        statusCode = ADD_MONEY_STATUS_CODES.SUCCESS;
    } else if (!balanceSuccess && eligibilitySuccess) {
        statusCode = ADD_MONEY_STATUS_CODES.BALANCE_FAILURE;
    } else if (balanceSuccess && !eligibilitySuccess) {
        statusCode = ADD_MONEY_STATUS_CODES.ELIGIBILITY_FAILURE;
    } else {
        statusCode = ADD_MONEY_STATUS_CODES.BALANCE_ELIGIBILITY_FAILURE;
    }

    return {
        statusCode,
        statusMessage: STATUS_CODE_MESSAGE_MAPPING[statusCode],
    };
};

const isFetchedResponseSuccessful = (response: ApiResponse<unknown>): boolean => {
    const isSuccessResponse = !!response.data && response.statusCode == 0;
    return isSuccessResponse;
};

export const transformSwiggyAddMoneyData = (
    responses: AddMoneyDataApiResponseType,
): ApiResponse<AddMoneyAggregateResponse> => {
    const balanceResponse = responses[0];
    const eligibilityResponse = responses[1];
    const offersResponse = responses[2];

    const isBalanceFetchingSuccessful = isFetchedResponseSuccessful(balanceResponse);
    const isEligibilityFetchingSuccessful = isFetchedResponseSuccessful(eligibilityResponse);
    const isOffersFetchingSuccessful = isFetchedResponseSuccessful(offersResponse);

    const offers = isOffersFetchingSuccessful ? offersResponse.data?.addMoney : [];

    const statusDetails = getAggregateStatusDetails(isBalanceFetchingSuccessful, isEligibilityFetchingSuccessful);
    if (
        statusDetails.statusCode !== ADD_MONEY_STATUS_CODES.SUCCESS ||
        isEmpty(balanceResponse.data) ||
        isEmpty(eligibilityResponse.data)
    ) {
        return {
            ...statusDetails,
            data: null,
        };
    }
    const aggregateResponse = {
        ...statusDetails,
        data: {
            eligibleBalance: balanceResponse.data.balancePresenter.available,
            eligibilityDetails: eligibilityResponse.data,
            offers: offers || [],
        },
    };
    return aggregateResponse;
};

export const getBalance = async (apiHost: string): Promise<ApiResponse<GetBalanceApiResponse>> => {
    const response = await makeDownstreamApiCall<ApiResponse<GetBalanceApiResponse>>({
        apiHost,
        endpoint: ENDPOINT.BALANCE_API,
    });

    return response;
};

export const checkAddMoneyEligibility = async (
    apiHost: string,
    billTotalShown: string,
): Promise<ApiResponse<GetEligibilityApiModel>> => {
    const response = await makeDownstreamApiCall<ApiResponse<GetEligibilityApiModel>>({
        apiHost,
        endpoint: ENDPOINT.CHECK_ADD_MONEY_ELIGIBILITY,
        searchParams: {
            billTotalShown,
        },
    });

    return response;
};

export const fetchAddMoneyOffers = async (apiHost: string): Promise<ApiResponse<GetOffersApiModel>> => {
    const response = await makeDownstreamApiCall<ApiResponse<GetOffersApiModel>>({
        apiHost,
        endpoint: ENDPOINT.FETCH_ADD_MONEY_OFFERS,
    });

    return response;
};

export async function fetchAddMoneyData(billTotalShown: string, apiHost: string): Promise<AddMoneyAggregateResponse> {
    const responses = (await Promise.all([
        getBalance(apiHost),
        checkAddMoneyEligibility(apiHost, billTotalShown),
        fetchAddMoneyOffers(apiHost),
    ])) as AddMoneyDataApiResponseType;
    const aggregatedResponse = transformSwiggyAddMoneyData(responses);

    if (aggregatedResponse.statusCode !== 0 || isEmpty(aggregatedResponse.data)) {
        return Promise.reject(aggregatedResponse);
    }
    return Promise.resolve(aggregatedResponse.data);
}
